import { memo, useCallback, useState } from 'react'

import { Button, Text } from '@cash-me/react-components'
import Image from 'next/image'
import { Controller, Virtual } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper/types'

import { Carousel } from 'components/carousel'
import { Col, Grid } from 'components/grid'
import useWindowSize from 'hooks/useWindowSize'
import { pushDataLayer } from 'utils/push-data-layer'

import { textCards, imageCards } from './data'
import * as S from './styles'

import 'swiper/css/virtual'

type Props = {
  onCtaClick: () => void
}

export const CondominiumCreditProjectUses = memo(({ onCtaClick }: Props) => {
  const { isTablet } = useWindowSize()
  const [textSwiper, setTextSwiper] = useState<SwiperType | null>(null)
  const [imageSwiper, setImageSwiper] = useState<SwiperType | null>(null)

  const handleChangeIndex = useCallback((index: number) => {
    const currentProject = textCards[index]

    if (!currentProject) return

    pushDataLayer({
      category: 'Slider',
      action: 'Crédito para Condomínios',
      label: `Projetos do condomínio - Slide ${currentProject.title}`
    })
  }, [])

  return (
    <S.Section aria-labelledby="condominium-project-uses-title">
      <Grid col={{ phone: 1, tablet: 1 }}>
        <Col col={{ desktop: 10 }} start={{ desktop: 2 }}>
          <S.Infos>
            <Text
              id="condominium-project-uses-title"
              as="h2"
              size="heading2"
              weight="medium"
              color="var(--colors-neutral-text-default)"
            >
              Para quais{' '}
              <Text as="span" size="heading2" weight="medium" color="var(--colors-pink-text-contrast)">
                projetos
              </Text>{' '}
              o condomínio pode usar o crédito?
            </Text>

            <Text color="var(--colors-neutral-text-default)">
              O Crédito para Condomínios pode ser avaliado para diferentes necessidades do empreendimento, sempre de
              acordo com a finalidade da operação e a análise de crédito.
            </Text>

            <S.ContentWrapper>
              <S.CarouselWrapper>
                <Carousel
                  navigation
                  onChangeIndex={handleChangeIndex}
                  setOutsideSwiperRef={setTextSwiper}
                  swiperOptions={{
                    modules: [Controller],
                    controller: {
                      control: imageSwiper
                    },
                    effect: 'fade',
                    loop: true
                  }}
                >
                  {textCards.map(({ id, icon, title, description }) => (
                    <S.Card key={`slider-text-${id}`}>
                      <div aria-hidden>{icon}</div>
                      <Text as="h3" size="heading3" color="var(--colors-neutral-text-contrast-default)">
                        {title}
                      </Text>
                      <Text size="bodySmall" color="var(--colors-neutral-text-contrast-default)">
                        {description}
                      </Text>
                    </S.Card>
                  ))}
                </Carousel>
              </S.CarouselWrapper>

              {isTablet ? (
                <S.CardsWrapper>
                  <Swiper
                    virtualTranslate={true}
                    modules={[Controller, Virtual]}
                    onSwiper={setImageSwiper}
                    controller={{ control: textSwiper }}
                    resistance={true}
                    resistanceRatio={0}
                    grabCursor={true}
                    loop
                  >
                    {imageCards.map(card => (
                      <SwiperSlide key={`slider-image-${card.id}`} virtualIndex={card.id}>
                        <Image
                          src={card.src}
                          alt={card.alt}
                          width="350"
                          height="370"
                          quality={100}
                          sizes="100vw"
                          loading="lazy"
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </S.CardsWrapper>
              ) : null}
            </S.ContentWrapper>

            <Button
              aria-label="Solicitar uma simulação de crédito para condomínio"
              className="quick-access"
              size="lg"
              type="button"
              variants="text-only"
              onClick={onCtaClick}
            >
              <span>Solicitar uma simulação</span>
            </Button>
          </S.Infos>
        </Col>
      </Grid>
    </S.Section>
  )
})

CondominiumCreditProjectUses.displayName = 'CondominiumCreditProjectUses'
